import { flow } from '@helo/utils'
import type { ReactNode } from 'react'

import { colors } from '../../../theme/colors'
import type { QueryOutput } from '../../Query'
import { RenderPipeline } from '../../RenderPipeline'
import { ChartAxisStyles } from '../ChartAxis/ChartAxis.constants'
import { ChartAxisUtils } from '../ChartAxis/ChartAxis.parts'
import type {
  ChartDataOutput,
  ChartDataPoint,
  ChartDataSlice,
  ResolvedChartSeries,
} from '../ChartData'
import type { ChartSeriesOutput } from '../ChartSeries'
import { useChartWrapperContext } from '../ChartWrapper/ChartWrapper'
import {
  type ChartDataLabelOptions,
  chartDataLabelOptions,
} from './ChartDataLabel.options'
import {
  ChartDataLabelInputParts,
  ChartDataLabelOutputParts,
} from './ChartDataLabel.parts'

export { chartDataLabelOptions } from './ChartDataLabel.options'
export type { ChartDataLabelOptions } from './ChartDataLabel.options'

export type ChartDataLabelInput = ChartDataLabelOptions

export type ChartDataLabelSlice = {
  showDataLabels: boolean
}

export type ChartDataLabelOutput = {
  dataLabel: ChartDataLabelSlice
}

export interface ChartDataLabelsRenderProps
  extends ChartDataLabelSlice,
    Pick<ChartDataSlice, 'dimensionKey'> {
  series: ResolvedChartSeries[]
}

export type ChartDataLabelPriorOutput = Pick<QueryOutput, 'contentNode'>

const chartDataLabelSetup = () =>
  RenderPipeline.createFeatureSetup<
    ChartDataLabelInput,
    ChartDataLabelOutput,
    ChartDataLabelPriorOutput
  >(
    flow(
      ChartDataLabelInputParts.setupInput(),
      ChartDataLabelOutputParts.setDataLabel(),
    ),
  )

export interface ChartDataLabelsProps {
  renderConfig: ChartDataLabelOutput & ChartDataOutput & ChartSeriesOutput
}

const DataLabels = ({ renderConfig }: ChartDataLabelsProps): ReactNode => {
  const { scales } = useChartWrapperContext()
  const { dataLabel, data, series } = renderConfig
  const { showDataLabels } = dataLabel
  const { dimensionKey } = data

  if (!showDataLabels || scales == null || dimensionKey == null) return null

  const isHorizontal = scales.orientation === 'horizontal'

  return (
    <g>
      {series.map(seriesItem =>
        seriesItem.points.map((point: ChartDataPoint, index) => {
          if (point.y == null) return null
          const fill =
            'color' in seriesItem && typeof seriesItem.color === 'string'
              ? seriesItem.color
              : colors.presets.neutral['0']
          const stackBase =
            'stackOffsets' in seriesItem &&
            Array.isArray(seriesItem.stackOffsets)
              ? (seriesItem.stackOffsets[index] ?? 0)
              : 0
          const measure = stackBase + point.y
          const bandwidth =
            'bandwidth' in (isHorizontal ? scales.yScale : scales.xScale) &&
            typeof (isHorizontal ? scales.yScale : scales.xScale).bandwidth ===
              'function'
              ? (isHorizontal ? scales.yScale : scales.xScale).bandwidth()
              : 0
          const categoryStart = isHorizontal
            ? (scales.yScale(point.x) ?? 0)
            : (scales.xScale(point.x) ?? 0)
          const categoryCenter = categoryStart + bandwidth / 2
          const x = isHorizontal
            ? (scales.xScale(measure) ?? 0) + ChartAxisUtils.dataLabelOffsetX
            : categoryCenter
          const y = isHorizontal
            ? categoryCenter
            : (scales.yScale(measure) ?? 0) - ChartAxisUtils.dataLabelOffsetY
          return (
            <text
              key={`${seriesItem.seriesKey}-label-${index}`}
              x={x}
              y={y}
              fill={fill}
              fontFamily={ChartAxisStyles.dataLabel.fontFamily}
              fontSize={ChartAxisStyles.dataLabel.fontSize}
              fontWeight={ChartAxisStyles.dataLabel.fontWeight}
              textAnchor={
                isHorizontal ? 'start' : ChartAxisStyles.dataLabel.textAnchor
              }
              dominantBaseline={isHorizontal ? 'middle' : undefined}
            >
              {seriesItem.formatDisplayValue?.(point.y) ?? String(point.y)}
            </text>
          )
        }),
      )}
    </g>
  )
}

export const ChartDataLabel = {
  options: chartDataLabelOptions,
  setup: chartDataLabelSetup,
  DataLabels,
}
